import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-preact';
import { Icon } from '../primitives/Icon';
import { Tooltip } from '../overlays/Tooltip';
import { UserMenu } from './UserMenu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { SidebarHoldContext } from './sidebarHold';
import { NAV, activeNavId } from '@/stores/navigation';
import type { NavItem } from '@/stores/navigation';
import { cn } from '@/lib/cn';
import { Link, useLocation } from 'wouter-preact';
import { useRecommendedActionsSummary } from '@/hooks/useRecommendedActions';

export interface SidebarProps {
  collapsed?: boolean;
  /** Sidebar fijada expandida (preferencia persistida en AppShell). */
  pinned?: boolean;
  /** Si existe, se muestra el toggle de fijar/colapsar (solo desktop). */
  onTogglePin?: () => void;
  onClose?: () => void;
  className?: string;
}

const HOVER_ENTER_MS = 180;
const HOVER_LEAVE_MS = 400;
const HOVER_LEAVE_AFTER_CLICK_MS = 1500;

export function Sidebar({
  collapsed = false,
  pinned = false,
  onTogglePin,
  onClose,
  className,
}: SidebarProps) {
  const active = activeNavId.value;
  const [path] = useLocation();
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const enterTimer = useRef<ReturnType<typeof setTimeout>>();
  const enterPending = useRef(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>();
  const clickedRef = useRef(false);
  const prevPathRef = useRef(path);
  const lastPointerDown = useRef<{ at: number; x: number; y: number } | null>(null);
  // Menús abiertos desde el raíl (selector de empresa, menú de usuario).
  const held = useRef(false);

  const visualCollapsed = collapsed && !hoverExpanded;

  useEffect(() => {
    if (!collapsed) setHoverExpanded(false);
  }, [collapsed]);

  // Navegar cierra el peek: sin esto, tras clicar un item el raíl queda
  // expandido tapando la página nueva hasta que el ratón sale o el focus se
  // va a otro elemento (el link clicado retiene el focus). Solo reacciona a
  // cambios de ruta reales — no debe deshacer el peek intencional que deja
  // abierto handleTogglePin al des-fijar (que no cambia la ruta).
  useEffect(() => {
    if (prevPathRef.current === path) return;
    prevPathRef.current = path;
    if (!collapsed) return;
    clearTimeout(enterTimer.current);
    enterPending.current = false;
    clearTimeout(leaveTimer.current);
    clickedRef.current = false;
    setHoverExpanded(false);
    // El link clicado retiene el focus y anclaría la próxima expansión por
    // focusin; tras navegar, el focus pertenece al contenido (patrón
    // skip-to-content). Solo si el focus sigue dentro del raíl.
    const el = containerRef.current;
    if (el && document.activeElement && el.contains(document.activeElement)) {
      document.getElementById('main-content')?.focus();
    }
  }, [path, collapsed]);

  useEffect(
    () => () => {
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
    },
    [],
  );

  // El último pointerdown se registra en todo el documento: al elegir una
  // opción de un menú en portal, el foco vuelve al disparador del raíl y ese
  // focusin no debe tratarse como navegación por teclado (re-expandía el raíl).
  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      lastPointerDown.current = { at: performance.now(), x: event.clientX, y: event.clientY };
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, []);

  const hold = useCallback(
    (next: boolean) => {
      held.current = next;
      clearTimeout(leaveTimer.current);
      if (next || !collapsed) return;
      // Al cerrar el menú, se pliega solo si el ratón ya no está sobre el raíl.
      if (containerRef.current?.matches(':hover')) return;
      leaveTimer.current = setTimeout(() => setHoverExpanded(false), HOVER_LEAVE_MS);
    },
    [collapsed],
  );

  // Expansión por teclado: focus dentro del raíl expande inmediato (sin el
  // delay de intención del hover); al salir el focus, colapsa con el mismo
  // delay que el ratón. Sin esto un usuario de teclado tabula por iconos
  // sin ver labels de grupo ni badges.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !collapsed) return;
    const onFocusIn = () => {
      // Solo focus de teclado: el focus que provoca un mousedown en el raíl
      // NO debe expandir — expandir a mitad de click re-renderiza y el
      // mouseup puede aterrizar en otro elemento (el click se pierde). La
      // expansión por puntero ya la gestionan mouseenter/mousemove.
      const down = lastPointerDown.current;
      if (down && performance.now() - down.at < 500) return;
      clearTimeout(enterTimer.current);
      enterPending.current = false;
      clearTimeout(leaveTimer.current);
      setHoverExpanded(true);
    };
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget as Node | null;
      if (held.current || (next && el.contains(next))) return;
      clearTimeout(leaveTimer.current);
      leaveTimer.current = setTimeout(() => setHoverExpanded(false), HOVER_LEAVE_MS);
    };
    el.addEventListener('focusin', onFocusIn);
    el.addEventListener('focusout', onFocusOut);
    return () => {
      el.removeEventListener('focusin', onFocusIn);
      el.removeEventListener('focusout', onFocusOut);
    };
  }, [collapsed]);

  const armEnterTimer = () => {
    if (enterPending.current) return;
    enterPending.current = true;
    enterTimer.current = setTimeout(() => {
      enterPending.current = false;
      setHoverExpanded(true);
    }, HOVER_ENTER_MS);
  };

  const clearEnterTimer = () => {
    clearTimeout(enterTimer.current);
    enterPending.current = false;
  };

  const handleMouseEnter = () => {
    clearTimeout(leaveTimer.current);
    if (!collapsed) return;
    clearEnterTimer();
    armEnterTimer();
  };

  // Tras colapsar por navegación el cursor puede seguir dentro del raíl:
  // mouseenter no volverá a disparar hasta salir y reentrar, así que sin esto
  // el raíl queda sordo al hover. El umbral de distancia respecto al último
  // pointerdown evita que el micro-movimiento del propio click reabra la
  // barra recién colapsada.
  const handleMouseMove = (event: MouseEvent) => {
    if (!collapsed || hoverExpanded || enterPending.current) return;
    const down = lastPointerDown.current;
    if (down && Math.hypot(event.clientX - down.x, event.clientY - down.y) < 24) return;
    armEnterTimer();
  };

  const handleMouseLeave = () => {
    clearEnterTimer();
    clearTimeout(leaveTimer.current);
    if (held.current) return;
    const delay = clickedRef.current ? HOVER_LEAVE_AFTER_CLICK_MS : HOVER_LEAVE_MS;
    clickedRef.current = false;
    leaveTimer.current = setTimeout(() => setHoverExpanded(false), delay);
  };

  const handlePointerDown = (event: PointerEvent) => {
    if (!hoverExpanded) return;
    // Retener abierta solo aplica a acciones internas (pin, user menu,
    // workspace switcher): un click en un link es navegación y el cierre lo
    // gestiona el efecto de cambio de ruta.
    const target = event.target as HTMLElement | null;
    if (target?.closest('a')) return;
    clickedRef.current = true;
    clearTimeout(leaveTimer.current);
  };

  const handleTogglePin = () => {
    // Al des-fijar, mantener el peek abierto hasta que el ratón/focus salga:
    // colapsar bajo el cursor se siente brusco.
    if (pinned) setHoverExpanded(true);
    onTogglePin?.();
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'group/sidebar relative h-full shrink-0',
        'duration-slow ease-smooth transition-[width]',
        collapsed ? 'w-[var(--sidebar-w-collapsed)]' : 'w-[var(--sidebar-w)]',
        hoverExpanded && 'z-30',
        className,
      )}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onPointerDown={handlePointerDown}
    >
      <aside
        className={cn(
          'flex h-full flex-col overflow-x-hidden',
          'border-border-subtle bg-canvas border-r',
          collapsed
            ? cn(
                'absolute inset-y-0 left-0 z-20',
                'duration-med ease-smooth transition-[width,box-shadow]',
                hoverExpanded ? 'w-[var(--sidebar-w)] shadow-xl' : 'w-[var(--sidebar-w-collapsed)]',
              )
            : 'relative',
        )}
        aria-label="Navegación principal"
      >
        <SidebarHoldContext.Provider value={hold}>
          <SidebarHeader collapsed={visualCollapsed} onClose={onClose} />
          <SidebarNav collapsed={visualCollapsed} active={active} />
          {onTogglePin ? (
            <SidebarPinToggle
              collapsed={visualCollapsed}
              pinned={pinned}
              onToggle={handleTogglePin}
            />
          ) : null}
          <SidebarFooter collapsed={visualCollapsed} />
        </SidebarHoldContext.Provider>
      </aside>
    </div>
  );
}

// ─── Pin toggle: fijar/colapsar la barra (desktop) ─────────────────────────
//
// Además de la preferencia, es la única vía de expansión en dispositivos
// touch con viewport desktop (sin hover) y un destino tabulable claro para
// teclado — un tap/Enter fija la barra expandida.

function SidebarPinToggle({
  collapsed,
  pinned,
  onToggle,
}: {
  collapsed: boolean;
  pinned: boolean;
  onToggle: () => void;
}) {
  const label = pinned ? 'Colapsar barra' : 'Fijar barra';
  const button = (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={pinned}
      aria-label={pinned ? 'Colapsar barra lateral' : 'Fijar barra lateral expandida'}
      className={cn(
        'group/item text-13 relative flex h-9 w-full items-center rounded-md outline-none',
        'duration-med ease-smooth transition-[background-color,color]',
        'text-fg-muted hover:bg-surface-hover hover:text-fg-strong',
        'focus-visible:shadow-focus focus-visible:ring-brand focus-visible:ring-1',
      )}
    >
      <span className="flex h-9 w-10 shrink-0 items-center justify-center">
        <Icon
          as={pinned ? PanelLeftClose : PanelLeftOpen}
          size={16}
          strokeWidth={1.5}
          className="text-fg-faint group-hover/item:text-fg-muted duration-med shrink-0 transition-colors"
        />
      </span>
      <span
        className={cn(
          'text-13 ease-smooth duration-med min-w-0 flex-1 truncate text-left transition-opacity',
          collapsed ? 'pointer-events-none opacity-0' : 'opacity-100',
        )}
      >
        {label}
      </span>
    </button>
  );

  return (
    <div className="shrink-0 px-2 pb-1">
      {/* Tooltip siempre montado (disabled cuando expandida): quitar/poner el
          wrapper recrea el botón y rompe un click o el focus en curso. */}
      <Tooltip label="Fijar barra expandida" placement="right" disabled={!collapsed}>
        {button}
      </Tooltip>
    </div>
  );
}

// ─── Footer: UserMenu ─────────────────────────────────────────────────────

function SidebarFooter({ collapsed }: { collapsed: boolean }) {
  return (
    <footer className="border-border-subtle flex shrink-0 items-center border-t px-2 py-2">
      <div className="min-w-0 flex-1">
        <UserMenu variant="sidebar" collapsed={collapsed} />
      </div>
    </footer>
  );
}

// ─── Header: workspace switcher + close (mobile) ───────────────────────

function SidebarHeader({ collapsed, onClose }: { collapsed: boolean; onClose?: () => void }) {
  return (
    <header className="border-border-subtle flex h-[var(--header-h)] shrink-0 items-center gap-2 border-b px-3">
      <div className="min-w-0 flex-1">
        <WorkspaceSwitcher collapsed={collapsed} />
      </div>

      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar menú"
          className={cn(
            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
            'text-fg-muted duration-med ease-smooth transition-colors outline-none',
            'hover:bg-surface-hover hover:text-fg-strong',
            'focus-visible:shadow-focus focus-visible:ring-brand focus-visible:ring-1',
          )}
        >
          <Icon as={X} size={16} />
        </button>
      ) : null}
    </header>
  );
}

// ─── Nav: groups + items ───────────────────────────────────────────────────

function SidebarNav({ collapsed, active }: { collapsed: boolean; active: string | null }) {
  // Call the hook once at the top — never inside a loop.
  const summary = useRecommendedActionsSummary();
  const recCount = summary.data.value?.open_count ?? 0;

  return (
    <nav className="no-scrollbar flex-1 overflow-x-hidden overflow-y-auto px-2 py-4">
      {NAV.map((group, groupIdx) => {
        const labelId = `sidebar-group-${group.id}-label`;
        const hasLabel = group.label.length > 0;
        // Inject badge into the 'resumen' item when count > 0: las acciones
        // recomendadas viven embebidas en Resumen (no hay item propio).
        const items: NavItem[] =
          recCount > 0
            ? group.items.map((it) =>
                it.id === 'resumen' ? { ...it, badge: String(recCount) } : it,
              )
            : group.items;
        return (
          <div key={group.id} className="mb-3 last:mb-2">
            {/* Slot de cabecera con altura FIJA en ambos estados: así al
                expandir/colapsar (hover) los items no se desplazan vertical
                ni horizontalmente. Colapsado: divisor centrado (o nada en el
                primer grupo). Expandido: label del grupo (o slot vacío si el
                grupo no tiene label, p.ej. el grupo principal). */}
            <div className="mb-1 flex h-4 items-center">
              {!collapsed && hasLabel ? (
                <span
                  id={labelId}
                  className="text-2xs tracking-display text-fg-faint truncate pr-3 pl-10 font-medium uppercase"
                >
                  {group.label}
                </span>
              ) : collapsed && groupIdx > 0 ? (
                <div className="bg-border-subtle mx-3 h-px w-full" aria-hidden="true" />
              ) : null}
            </div>

            {/* Sin role="group": sobre un <ul> anula el rol implícito de lista
                y deja los <li> huérfanos (axe `listitem`, serious). El <ul>
                etiquetado por aria-label/aria-labelledby ya es una lista
                accesible y agrupa la sección de nav. */}
            <ul
              className="flex flex-col gap-1"
              {...(!collapsed && hasLabel
                ? { 'aria-labelledby': labelId }
                : { 'aria-label': group.label || 'Navegación principal' })}
            >
              {items.map((it) => (
                <SidebarItem
                  key={it.id}
                  item={it}
                  active={active === it.id}
                  collapsed={collapsed}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

interface SidebarItemProps {
  item: (typeof NAV)[number]['items'][number];
  active: boolean;
  collapsed: boolean;
}

function SidebarItem({ item: it, active, collapsed }: SidebarItemProps) {
  const linkClassName = cn(
    'group/item text-13 relative flex h-9 items-center rounded-md outline-none',
    'duration-med ease-smooth transition-[background-color,color]',
    'focus-visible:shadow-focus focus-visible:ring-brand focus-visible:ring-1',
    active
      ? 'bg-brand-tint text-brand font-medium'
      : 'text-fg-muted hover:bg-surface-hover hover:text-fg-strong',
  );
  const content = (
    <>
      {/* Slot de icono de anchura fija (= raíl colapsado): el icono queda
          centrado en el raíl y NO se mueve al expandir. */}
      <span className="relative flex h-9 w-10 shrink-0 items-center justify-center">
        <Icon
          as={it.icon}
          size={16}
          strokeWidth={active ? 2 : 1.5}
          className={cn(
            'duration-med shrink-0 transition-colors',
            active ? 'text-brand' : 'text-fg-faint group-hover/item:text-fg-muted',
          )}
        />
        {/* Dot: en el raíl el badge numérico se desvanece — sin esto un
            count pendiente es invisible con la barra colapsada. */}
        {it.badge ? (
          <span
            aria-hidden="true"
            className={cn(
              'bg-brand absolute top-1.5 right-2 h-1.5 w-1.5 rounded-full',
              'duration-med ease-smooth transition-opacity',
              collapsed ? 'opacity-100' : 'opacity-0',
            )}
          />
        ) : null}
      </span>
      {/* Label en flex-1: su anchura la mueve la animación de ancho del panel
          (sin saltos). Solo animamos opacidad → deslizamiento limpio. */}
      <span
        className={cn(
          'text-13 ease-smooth duration-med min-w-0 flex-1 truncate transition-opacity',
          collapsed ? 'pointer-events-none opacity-0' : 'opacity-100',
        )}
      >
        {it.label}
      </span>
      {it.badge ? (
        <span
          className={cn(
            'text-3xs mr-3 shrink-0 rounded-full px-1.5 py-0.5 leading-4 font-semibold tabular-nums',
            'duration-med ease-smooth transition-opacity',
            collapsed ? 'opacity-0' : 'opacity-100',
            active ? 'bg-brand text-brand-contrast' : 'bg-brand-tint text-brand',
          )}
        >
          {it.badge}
        </span>
      ) : null}
    </>
  );
  const link = it.openInNewWindow ? (
    <a
      href={it.path}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${it.label} (se abre en una ventana nueva)`}
      className={linkClassName}
    >
      {content}
    </a>
  ) : (
    <Link href={it.path} aria-current={active ? 'page' : undefined} className={linkClassName}>
      {content}
    </Link>
  );

  // Tooltip SIEMPRE en el árbol (disabled cuando expandida): alternar el
  // wrapper recrea el <a>, y si la barra se expande a mitad de un click
  // (mousedown → focus → re-render → mouseup) el click aterriza en el
  // ancestro común en vez del link y la navegación se pierde. También
  // preserva el focus de teclado al expandir/colapsar.
  return (
    <li>
      <Tooltip label={it.label} placement="right" disabled={!collapsed}>
        {link}
      </Tooltip>
    </li>
  );
}
